/**
 * Ryan O'Dowd
 * 2022-08-28
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  GOAL_ROUTE,
  apiSavePublicGoal,
  setActiveGoalId,
} from '@utils/store/actions';
import React, {
  useState,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import Button from '@components/common/Button';
import Icon from '@react-native-vector-icons/material-design-icons';
import ListItem from '@components/ListItem';
import Popover from '@components/common/Popover';
import Tag from './Tag';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const GOAL_TYPES = {
  reading: {
    title: 'Reading plan',
    iconName: 'book-open-page-variant',
  },
  memory: {
    title: 'Memory-verse plan',
    iconName: 'school',
  },
  group: { // @TODO: shouldn't need this type on this screen...none of these should be public, right? maybe church groups...
    title: 'Group goal',
    iconName: 'account-group',
  },
};

const PublicGoalPreview = (_p: PublicGoalPreviewProps) => {
  const props = {
    ...defaultProps,
    ..._p,
  };

  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  const preferredTranslation = hooks.usePreferredTranslation();
  const userGoals = hooks.useTypedSelector((state) => state.goals);
  const [_goalAdded, setGoalAdded] = useState(false);

  const isAlreadyAGoal = !!Object.values(userGoals).find((p: any) => p.id === props.id);

  const betaOn = hooks.useTypedSelector((state) => state.userSettings.beta_on);

  return (
    <ListItem
      onPress={() => {
        if (isAlreadyAGoal) {
          dispatch(setActiveGoalId(props.id));
        }
        navigation.navigate(GOAL_ROUTE, isAlreadyAGoal ? undefined : {
          previewGoalId: props.id,
        });
      }}
    >{/* @TODO: this is not full width on desktop */}
      <View style={styles.firstRow}>
        <View style={styles.textWrapper}>
          <View style={styles.titleAndTypeWrapper}>
            <Text style={styles.title}>{props.title}</Text>
            <Popover.Wrapper
              popover={
                <Popover.Inner>
                  <Popover.Text title={true}>{GOAL_TYPES[props.type].title}</Popover.Text>
                </Popover.Inner>
              }
            >
              <View style={styles.goalTypeButton}>
                <Icon
                  style={styles.goalTypeIcon}
                  name={GOAL_TYPES[props.type].iconName}
                />
              </View>
            </Popover.Wrapper>
          </View>
          {props.description &&
            <Text style={styles.description}>
              {props.description}
            </Text>
          }
        </View>
      </View>
      <ScrollView
        keyboardShouldPersistTaps='handled'
        contentContainerStyle={styles.tagsWrapper}
        horizontal={true}
      >
        {props.tags?.sort().map((tag) => <Tag key={tag.id} name={tag.name} onPress={props.onTagPress} />)}
      </ScrollView>
      <View style={styles.lastRow}>
        <Button
          containerStyle={styles.addButton}
          mode='contained'
          disabled={isAlreadyAGoal || _goalAdded}
          icon={isAlreadyAGoal ? 'check' : 'plus'}
          theme={isAlreadyAGoal ? styles.colors.primary : styles.colors.accent}
          onPress={() => {
            setGoalAdded(true);
            dispatch(apiSavePublicGoal(props.id, preferredTranslation.id));
          }}
        >
          {isAlreadyAGoal ? i18n('goalAdded') : i18n('addGoal')}
        </Button>
        {/* @TODO: put back when there are enough people doing these (num people and difficulty level)
        <View style={styles.statsWrapper}>{/* @TODO: abstract to a new component and surround with popover tooltip explaining what the symbols are /}
          <Text style={styles.statText}>
            <Icon style={styles.statIcon} name='account'/>1.4k/* @TODO: don't hardcode /
          </Text>
          <Text style={styles.statText}>
            <Icon style={styles.statIcon} name='signal-cellular-2'/> 23{/* @TODO: don't hardcode (1, 2, 3 are valid icon names); also change color based on difficulty (or actually, maybe make this info instead...not sure if difficulty is objective enough); on click, popover has number of passages, verses, words /}
          </Text>
        </View>
        */}

        {!!betaOn &&
          <View style={styles.userCountWrapper}>
            <Icon
              style={styles.goalTypeIcon}
              name='account'// @TODO: fire?
            />
            <Text style={styles.userCount}>{utilities.humanReadableNumber(props.userCount)}</Text>
          </View>
        }
      </View>
    </ListItem>
  );
};

class PublicGoalPreviewProps {
  id: number;
  title: string;
  description: string;
  tags: any[];
  onTagPress: (t: any) => void;
  userCount: number;
  type: string = 'memory'; // @TODO: oneOf(Object.keys(GOAL_TYPES)) = 'memory';
};
const defaultProps = new PublicGoalPreviewProps();

export default PublicGoalPreview;
